/**
 * A ~150-line keyed virtual DOM.
 *
 * The design is one big tree of inline-styled divs, which maps onto hyperscript
 * almost literally — so rather than pull in a framework we patch the real DOM
 * directly. Two properties matter for this app:
 *
 *   - form fields stay UNCONTROLLED. We never write `.value` after creation, so
 *     typing survives a re-render and the caller clears fields explicitly.
 *   - list rows carry a `key`, so deleting the middle row of a list moves the
 *     surviving rows instead of rewriting them (and their inputs).
 */

const TEXT = Symbol('text');

const SVG_NS = 'http://www.w3.org/2000/svg';
const SVG_TAGS = new Set(['svg', 'g', 'path', 'line', 'rect', 'circle', 'polyline', 'polygon', 'defs', 'clipPath', 'linearGradient', 'stop']);

const isVNode = value => value !== null && typeof value === 'object' && value.__vnode === true;

function textNode(value) {
  return { __vnode: true, tag: TEXT, text: String(value), props: {}, children: [], key: null };
}

export function h(tag, props, ...children) {
  const flat = [];
  const push = child => {
    if (child === null || child === undefined || child === false || child === true) return;
    if (Array.isArray(child)) return child.forEach(push);
    flat.push(isVNode(child) ? child : textNode(child));
  };
  children.forEach(push);

  const attrs = props || {};
  return {
    __vnode: true,
    tag,
    props: attrs,
    children: flat,
    key: attrs.key === undefined ? null : attrs.key
  };
}

function applyProps(el, next, prev = {}) {
  for (const name of Object.keys(prev)) {
    if (name in next || name === 'key' || name === 'ref') continue;
    if (name.startsWith('on')) {
      el[name.toLowerCase()] = null;
    } else {
      el.removeAttribute(name === 'className' ? 'class' : name);
    }
  }

  for (const [name, value] of Object.entries(next)) {
    if (name === 'key' || name === 'ref') continue;

    if (name.startsWith('on')) {
      // Assigning to the on* property replaces the previous handler outright,
      // so repeated patches cannot stack listeners.
      el[name.toLowerCase()] = typeof value === 'function' ? value : null;
      continue;
    }
    if (prev[name] === value) continue;

    const attr = name === 'className' ? 'class' : name;
    if (value === null || value === undefined || value === false) {
      el.removeAttribute(attr);
    } else if (value === true) {
      el.setAttribute(attr, '');
    } else {
      el.setAttribute(attr, String(value));
    }
  }
}

function createElement(vnode) {
  if (vnode.tag === TEXT) {
    const node = document.createTextNode(vnode.text);
    vnode.el = node;
    return node;
  }

  /* An SVG element only draws when it is made in the SVG namespace. */
  const el = SVG_TAGS.has(vnode.tag)
    ? document.createElementNS(SVG_NS, vnode.tag)
    : document.createElement(vnode.tag);
  applyProps(el, vnode.props);
  vnode.children.forEach(child => el.appendChild(createElement(child)));
  vnode.el = el;

  if (typeof vnode.props.ref === 'function') vnode.props.ref(el);
  // `value` seeds the field once; from then on the DOM owns it.
  if (vnode.props.value !== undefined && 'value' in el) el.value = vnode.props.value;

  return el;
}

const sameType = (a, b) => a.tag === b.tag && a.key === b.key;

function patchNode(oldVNode, newVNode) {
  const el = oldVNode.el;
  newVNode.el = el;

  if (newVNode.tag === TEXT) {
    if (newVNode.text !== oldVNode.text) el.data = newVNode.text;
    return;
  }

  applyProps(el, newVNode.props, oldVNode.props);
  if (typeof newVNode.props.ref === 'function') newVNode.props.ref(el);
  patchChildren(el, newVNode.children, oldVNode.children);
}

function patchChildren(parent, nextChildren, prevChildren) {
  const keyed = nextChildren.length > 0
    && nextChildren.every(c => c.key !== null)
    && prevChildren.every(c => c.key !== null);

  if (!keyed) {
    const length = Math.max(nextChildren.length, prevChildren.length);
    for (let i = 0; i < length; i++) {
      const next = nextChildren[i];
      const prev = prevChildren[i];

      if (!prev) parent.appendChild(createElement(next));
      else if (!next) prev.el.remove();
      else if (sameType(next, prev)) patchNode(prev, next);
      else parent.replaceChild(createElement(next), prev.el);
    }
    return;
  }

  const byKey = new Map(prevChildren.map(child => [child.key, child]));

  nextChildren.forEach((next, index) => {
    const prev = byKey.get(next.key);
    let el;

    if (prev && prev.tag === next.tag) {
      patchNode(prev, next);
      el = next.el;
      byKey.delete(next.key);
    } else {
      if (prev) {
        prev.el.remove();
        byKey.delete(next.key);
      }
      el = createElement(next);
    }

    if (parent.childNodes[index] !== el) {
      parent.insertBefore(el, parent.childNodes[index] || null);
    }
  });

  byKey.forEach(child => child.el.remove());
}

/**
 * Binds a render function to a container. Calling the returned `update` patches
 * the existing DOM in place rather than rebuilding it.
 */
export function mount(container, render) {
  let previous = null;

  return function update(...args) {
    const next = render(...args);

    if (!previous) {
      container.textContent = '';
      container.appendChild(createElement(next));
    } else if (sameType(next, previous)) {
      patchNode(previous, next);
    } else {
      container.replaceChild(createElement(next), previous.el);
    }

    previous = next;
    return next;
  };
}
